/**
 * Centralized Shipping Configuration
 * ===================================
 * 
 * Single source of truth for shipping-related constants.
 * Must match backend/utils/shipping_rules.py values.
 * 
 * Rules:
 * - B2C: Free shipping from €150 subtotal (after discounts)
 * - B2C: Standard shipping €15.00 below threshold
 * - B2B: Always free shipping
 */

// Threshold for free shipping (B2C only)
export const FREE_SHIPPING_THRESHOLD = 150.00;

// Standard shipping cost when below threshold
export const STANDARD_SHIPPING_COST = 15.00;

/**
 * Calculate shipping info for B2C users
 * @param {number} subtotalAfterDiscounts - Cart subtotal after discounts applied
 * @returns {object} Shipping info with cost, isFree, amountToFree, message
 */
export const calculateB2CShipping = (subtotalAfterDiscounts) => {
  const safeSubtotal = Math.max(0, subtotalAfterDiscounts || 0);
  
  if (safeSubtotal >= FREE_SHIPPING_THRESHOLD) {
    return {
      shippingCost: 0,
      isFree: true,
      amountToFree: 0,
      message: 'Gratis verzending toegepast',
      thresholdReached: true
    };
  }
  
  const amountToFree = FREE_SHIPPING_THRESHOLD - safeSubtotal;
  return {
    shippingCost: STANDARD_SHIPPING_COST,
    isFree: false,
    amountToFree: Math.round(amountToFree * 100) / 100,
    message: `Nog €${amountToFree.toFixed(2)} voor gratis verzending`,
    thresholdReached: false
  };
};

/**
 * Get the free shipping progress percentage
 * @param {number} subtotalAfterDiscounts - Cart subtotal after discounts
 * @returns {number} Progress percentage (0-100)
 */
export const getFreeShippingProgress = (subtotalAfterDiscounts) => {
  const safeSubtotal = Math.max(0, subtotalAfterDiscounts || 0);
  const progress = (safeSubtotal / FREE_SHIPPING_THRESHOLD) * 100;
  return Math.min(100, Math.round(progress));
};
